import { Component } from '@angular/core';
import { BaseCellRendererComponent, Params } from '@examdojo/ag-grid';
import { connectState } from '@examdojo/angular/util';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReviewPermission } from '@examdojo/review';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { map, tap, combineLatest, distinctUntilChanged, merge } from 'rxjs';
import { MatCheckbox } from '@angular/material/checkbox';
import { shareOneReplay } from '@examdojo/core/rxjs';
import { AdminUserQuery } from '../admin-user.query';
import { AdminUserDetailFormService } from '../detail-view/admin-user-detail-form.service';
import {
  ReviewPermissionsListViewData,
  SyllabusPermissionStringBySyllabusId,
} from './review-permissions-list-view.model';

type ReviewPermissionCellRendererValue = boolean;
type ReviewPermissionCellRendererData = ReviewPermissionsListViewData;

type ParamsType = Params<
  ReviewPermissionCellRendererValue,
  ReviewPermissionCellRendererData,
  {
    reviewMode: ReviewPermission;
  }
>;

@Component({
  selector: 'exam-review-permission-cell-renderer',
  template: ` <mat-checkbox color="primary" class="mb-1 mt-1" [formControl]="ctrl" />`,
  host: {
    class: 'w-full flex flex-col justify-between md:justify-center md:gap-1 py-4 h-full md:py-0',
  },
  imports: [MatCheckbox, ReactiveFormsModule],
})
export class ReviewPermissionCellRendererComponent extends BaseCellRendererComponent<
  boolean,
  ReviewPermissionsListViewData,
  ParamsType
> {
  constructor(
    private readonly adminUserDetailFormService: AdminUserDetailFormService,
    private readonly adminUserQuery: AdminUserQuery,
  ) {
    super();

    merge(
      this.setPermissionFormValueOnControlChange(),
      this.setFormControlValueOnValueChange(),
      this.disableControlIfUserIsAdmin(),
    )
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  readonly ctrl = new FormControl<boolean>(false, { nonNullable: true });

  private readonly formControlName$ = combineLatest([
    this.data$.pipe(map((data) => data?.syllabus.id)),
    this.params$.pipe(map((params) => params?.reviewMode)),
  ]).pipe(
    map(([data, reviewMode]) => `${data}:${reviewMode}` as SyllabusPermissionStringBySyllabusId),
    shareOneReplay(),
  );

  private readonly isPermissionModeAllowed$ = this.formControlName$.pipe(
    map((formControlName) =>
      this.adminUserDetailFormService.form.controls['allowedModesWithSyllabusId'].value.includes(formControlName),
    ),
    distinctUntilChanged(),
  );

  readonly state = connectState({
    formControlName: this.formControlName$,
    isUserAdmin: this.adminUserQuery.isUserAdmin$,
  });

  private setFormControlValueOnValueChange() {
    return this.isPermissionModeAllowed$.pipe(
      tap((isAllowed) => {
        this.ctrl.setValue(isAllowed, { emitEvent: false });
      }),
    );
  }

  private setPermissionFormValueOnControlChange() {
    return this.ctrl.valueChanges.pipe(
      tap(() => {
        this.adminUserDetailFormService.togglePermissionMode(this.state.formControlName);
      }),
    );
  }

  private disableControlIfUserIsAdmin() {
    return this.adminUserQuery.isUserAdmin$.pipe(
      tap((isAdmin) => {
        if (isAdmin) {
          this.ctrl.disable({ emitEvent: false, onlySelf: true });
        } else {
          this.ctrl.enable({ emitEvent: false, onlySelf: true });
        }
      }),
    );
  }
}
